import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";
import { HailArticleLink } from "@/components/site/HailArticleLink";
import { hailArticleQuery } from "@/lib/hail-article-query";

const SLUG = "/storm-damage-repair";
const NAME = "Storm & Hail Damage Roof Repair";
const TITLE =
  "Storm & Hail Damage Roof Repair in Phoenix | Insurance Claims | Durafoam Roofing";
const DESCRIPTION =
  "Hail or monsoon damage? Durafoam documents it, meets your adjuster, and gets it fixed right — free storm inspection, same-week scheduling. Family-owned since 1989.";

const faqs = [
  {
    q: "Do you charge for a storm damage inspection?",
    a: "No. The storm inspection, the photo documentation, and the written scope are free, whether or not you end up filing a claim.",
  },
  {
    q: "Will you meet my insurance adjuster at my house?",
    a: "Yes. Tell us the appointment time and we'll be on the roof with your adjuster, pointing out damage and answering questions about repair scope in person.",
  },
  {
    q: "Should I file a claim before or after you look at the roof?",
    a: "Let us look first. If the damage clearly isn't claim-worthy, we'll tell you that and quote the repair directly — a denied claim on your record helps nobody.",
  },
  {
    q: "Do you handle the repair after the claim is approved?",
    a: "Yes. We do the actual roofing work ourselves — foam, tile, shingle, and flat systems — so the crew that documented the damage is the crew that fixes it.",
  },
  {
    q: "How fast can you tarp an active leak after a storm?",
    a: "Call us and we'll get emergency mitigation scheduled right away, then come back for the permanent repair once the claim is settled.",
  },
];

export const Route = createFileRoute("/storm-damage-repair")({
  loader: async ({ context }) => {
    const [settings] = await Promise.all([loadPageSettings(SLUG), context.queryClient.ensureQueryData(hailArticleQuery)]);
    return settings;
  },
  errorComponent: () => <div className="px-4 py-12" role="alert">This page could not load. Call <a href="tel:+14809415373" className="text-primary underline">480-941-5373</a> for a storm inspection.</div>,
  notFoundComponent: () => <div className="px-4 py-12"><h1>Storm repair page not found</h1><Link to="/">Return home</Link></div>,
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: NAME,
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="storm-damage-repair"
      eyebrow="Storm & hail damage"
      h1="Storm & Hail Damage Roof Repair in Phoenix — Fast Response, We Work Directly With Your Insurance"
      subhead="We document the damage, meet your adjuster on the roof, and do the repair ourselves. Free storm inspection, fast response."
      heroBullets={[
        "Free photo documentation",
        "We meet your adjuster",
        "Fast response",
        "Licensed ROC223900",
      ]}
      ctaLabel="Book My Storm Inspection"
      whatIs={{
        heading: "Free Storm Damage Inspection — Photos & Documentation Included",
        body: (
          <>
            <p>
              After a hailstorm or a hard monsoon cell, the damage that matters is usually the
              damage you can't see from the driveway. We get on the roof and inspect it the way an
              adjuster will: hail bruising on tile and shingle, torn or displaced tile, punctured or
              blistered foam, lifted flashings, bent vents and jacks, damaged skylight domes, and
              wind-driven water at the parapets.
            </p>
            <p>
              You get a written report with dated, labeled photos of every damaged area, a plain
              description of what caused it, and a scope of the repair work required. That packet is
              what turns a vague phone call to your carrier into a claim they can actually process.
            </p>
            <p>
              If we look and the roof is fine, we'll say so. Plenty of storm calls end with "your
              roof held up — here's a photo set for your records."
            </p>
            <p>
              Hail hit your neighborhood? <Link to="/hail-damage-roof-inspection-phoenix">Book a free hail damage roof inspection in Phoenix or Mesa</Link>
              <HailArticleLink prefix=", or read ">what hail does to foam roofs and why early documentation matters</HailArticleLink>.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "What Counts as Insurable Storm Damage (and What Doesn't)",
        body: (
          <>
            <p>
              Homeowners policies cover sudden damage from a specific event. They don't cover a roof
              that simply wore out. Knowing which side of that line you're on before you call your
              carrier saves you a denied claim.
            </p>
            <p>
              <strong>Usually covered:</strong>
            </p>
            <ul>
              <li>Hail bruising, cracking, or granule loss traceable to a dated storm</li>
              <li>Wind-lifted or missing tile, shingles, or cap sheet</li>
              <li>Punctures and impact damage to foam roofing from hail or debris</li>
              <li>Flashing, vent, and skylight damage from wind or impact</li>
              <li>Interior water damage that resulted from the storm-caused opening</li>
            </ul>
            <p>
              <strong>Usually not covered:</strong>
            </p>
            <ul>
              <li>UV-degraded foam coating that's simply due for a recoat</li>
              <li>Tile underlayment that has aged out after 20–25 years</li>
              <li>Leaks from deferred maintenance or a previous bad repair</li>
              <li>Ponding from a drainage design problem that predates the storm</li>
            </ul>
            <p>
              When the damage is wear rather than weather, we quote it as normal work — see{" "}
              <Link to="/roof-repair-cost-phoenix">what Phoenix roof repairs typically cost</Link>{" "}
              or, for foam roofs, our{" "}
              <Link to="/foam-roof-coating-recoating">foam recoating service</Link>.
            </p>
            <p>
              For a deeper walkthrough of what to do in the hours right after a storm, read{" "}
              <Link to="/blog/$slug" params={{ slug: "storm-damage-roof-repair-phoenix" }}>
                our Phoenix storm damage guide
              </Link>
              .
            </p>
          </>
        ),
      }}
      process={{
        heading: "Timeline: Inspection to Approved Repair",
        intro:
          "Most storm claims we handle move from first call to finished repair in two to four weeks, depending on how fast your carrier schedules the adjuster.",
        steps: [
          {
            title: "Day 1 — Call and emergency mitigation",
            body: "We schedule your free inspection, and if water is actively coming in we tarp or temporarily seal it the same visit so the interior damage stops growing.",
          },
          {
            title: "Days 1–3 — Inspection and documentation",
            body: "Full roof inspection with dated photos, a written scope of damage, and a repair estimate you can hand straight to your carrier.",
          },
          {
            title: "Days 3–10 — Adjuster meeting",
            body: "You file the claim; we meet the adjuster on the roof, walk the damage with them, and make sure nothing legitimate gets left off the scope.",
          },
          {
            title: "Days 10–14 — Scope agreement",
            body: "If the adjuster's scope misses items we documented, we submit the photos and supplement it before work starts, so you aren't paying the difference.",
          },
          {
            title: "Weeks 2–4 — Repair and final walkthrough",
            body: "Our own crews do the work, we handle permits where required, and we walk the finished roof with you before invoicing.",
          },
        ],
      }}
      whyChoose={{
        heading: "Why Homeowners Choose Durafoam Over the Storm-Chaser Crews",
        body: (
          <>
            <p>
              After every big Valley storm, out-of-town crews show up knocking doors, promising a
              free roof and a signature on the spot. Some are fine. Many are gone by next
              monsoon — along with the workmanship warranty they promised.
            </p>
            <ul>
              <li>
                <strong>We're actually here.</strong> Family-owned and roofing the Valley since
                1989, from a permanent Phoenix address — not a truck with out-of-state plates.
              </li>
              <li>
                <strong>Licensed and accountable.</strong> Arizona ROC223900, fully insured, with a
                paper trail on every job we've done.
              </li>
              <li>
                <strong>Our crews, not a subcontractor lottery.</strong> The people who documented
                your damage are the people on your roof.
              </li>
              <li>
                <strong>No pressure to sign in your driveway.</strong> You get the photos and the
                scope, and you decide whether a claim even makes sense.
              </li>
              <li>
                <strong>We'll still be here for the warranty.</strong> Roofs we installed in the
                1990s are still on our maintenance schedule.
              </li>
            </ul>
            <p>
              We also handle non-storm work on the same roofs — see{" "}
              <Link to="/roof-repair">general roof repair</Link> and{" "}
              <Link to="/emergency-roof-leak-repair">emergency leak response</Link>.
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Get Your Free Storm Inspection Scheduled This Week"
      ctaBody="Call us or send your details and we'll get on your roof, document everything, and tell you straight whether it's a claim or a repair."
      formProps={{
        eyebrow: "Free storm inspection",
        heading: "Request your storm inspection",
        subhead:
          "Tell us when the storm hit and what you're seeing. We'll schedule an inspection this week and bring the documentation with us.",
        messagePlaceholder: "What are you seeing? Any active leaks? (optional)",
        submitLabel: "Book my storm inspection",
        source: "storm-damage-inspection",
        serviceTag: "Storm & Hail Damage Repair",
      }}
    />
  );
}
